import { FileText, MessageSquare, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useState } from "react";
import { FormattedClinicalText } from "./FormattedClinicalText";
import { extractNoteText } from "@/utils/html-to-text";
import { cn } from "@/lib/utils";

interface ImagingAttachment {
  type: 'pdf' | 'image' | 'text' | 'html' | 'binary' | 'link';
  title: string;
  data: string;
  contentType: string;
}

interface ImagingAttachmentModalProps {
  attachment: ImagingAttachment | null;
  studyType?: string;
  studyDate?: string;
  open: boolean;
  onClose: () => void;
  onAskQuestion?: (question: string, displayText?: string) => void;
}

export function ImagingAttachmentModal({ attachment, studyType, studyDate, open, onClose, onAskQuestion }: ImagingAttachmentModalProps) {
  const { t } = useTranslation();
  const [selectedText, setSelectedText] = useState("");
  const [showAskPopup, setShowAskPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  if (!attachment) return null;

  const displayText = extractNoteText({
    type: attachment.type,
    data: attachment.data,
    contentType: attachment.contentType
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTextSelection = (event: React.MouseEvent) => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      setSelectedText(selection.toString().trim());
      setPopupPosition({ x: event.clientX, y: event.clientY });
      setShowAskPopup(true);
    }
  };

  const handleAskAboutSelection = () => {
    if (onAskQuestion && selectedText) {
      onAskQuestion(t('explain.imagingSelection', { selection: selectedText }));
    }
    setShowAskPopup(false);
    setSelectedText("");
    onClose();
  };

  const handleExplainAttachment = () => {
    if (onAskQuestion) {
      const reportText = displayText.substring(0, 2000);
      const dateSuffix = studyDate ? ` from ${formatDate(studyDate)}` : '';
      const fullQuestion = t('explain.imagingReportQuestion', {
        studyType: studyType || t('records.imagingStudyFallback'),
        dateSuffix,
        reportText,
      });
      const shortDisplayText = t('explain.imagingReportDisplay', {
        title: attachment.title || studyType || t('records.imagingReportFallbackLower'),
      });
      onAskQuestion(fullQuestion, shortDisplayText);
    }
    onClose();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "max-md:bg-black/80 md:bg-black/20"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "flex flex-col fixed z-50 bg-white shadow-lg duration-200 h-[90vh]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // Mobile: bottom sheet
            "inset-x-0 bottom-0 border-t",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            // Desktop: center modal
            "md:inset-x-0 md:left-[50%] md:top-[50%] md:right-auto md:bottom-auto md:translate-x-[-50%] md:translate-y-[-50%] md:w-full md:max-w-3xl md:border md:bg-background",
            "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95",
            "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
            "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
            "md:rounded-lg"
          )}
        >
          {/* Header */}
          <div className="flex items-start gap-3 pb-3 border-b px-6 pt-6 flex-shrink-0">
            <div className="text-gray-600">
              <FileText className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {attachment.title || studyType || t('records.imagingReportFallback')}
              </h3>
              <div className="text-sm text-text-secondary mt-1">
                {studyType && studyType !== attachment.title ? `${studyType} • ` : ''}
                {studyDate ? formatDate(studyDate) : ''}
              </div>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div
            className="text-text-primary px-6 py-4 flex-1 pointer-events-auto overflow-y-auto"
            onMouseUp={handleTextSelection}
            style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}
            tabIndex={0}
          >
            <FormattedClinicalText text={displayText} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t px-6 pb-6 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              {t('records.close')}
            </Button>
            {onAskQuestion && (
              <Button
                size="sm"
                onClick={handleExplainAttachment}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {t('explain.buttonNoteLabel')}
              </Button>
            )}
          </div>

          {/* Text selection popup */}
          {showAskPopup && (
            <div
              className="fixed z-[100] bg-white border border-gray-200 rounded-lg shadow-lg p-2"
              style={{ left: popupPosition.x, top: popupPosition.y - 40 }}
              onMouseLeave={() => setShowAskPopup(false)}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAskAboutSelection}
                className="text-blue-600 hover:bg-blue-50"
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                {t('records.askAboutThis')}
              </Button>
            </div>
          )}

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">{t('records.close')}</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
